/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type { JiraSpaRoute, Query, RouterContext } from '@atlassian/react-resource-router';
import type { RouteName } from '@jira/platform__routing-route-name-types/src/RouteName.tsx';
import {
	ENTITY_ID,
	MENU_ID_COMMUNICATIONS_STAKEHOLDERS,
	MENU_ID_OPERATIONS,
	MENU_ID_OPS_ALERTS,
	MENU_ID_OPS_CHANGES,
	MENU_ID_OPS_INCIDENTS,
	MENU_ID_OPS_ON_CALL,
	MENU_ID_OPS_OVERVIEW,
	MENU_ID_OPS_REPORTS,
	MENU_ID_OPS_STATUS_PAGES,
	MENU_ID_OPS_SERVICES,
} from '../../../../constants.tsx';
import type { SelectedPath } from '../../../../types.tsx';

const routes = new Set<RouteName>([
	'eoc-automations',
	'eoc-custom-roles',
	'eoc-global-alerts',
	'eoc-global-alerts-redirect',
	'eoc-global-alert-detail',
	'eoc-global-group-detail',
	'eoc-global-reports',
	'eoc-global-report-dashboard',
	'eoc-group-policy',
	'eoc-heartbeats',
	'eoc-global-heartbeats',
	'eoc-maintenance',
	'eoc-on-call',
	'eoc-on-call-redirect',
	'eoc-overview',
	'eoc-overview-all-teams',
	'eoc-permissions',
	'eoc-reports',
	'eoc-reports-team',
	'eoc-reports-unsubscribe',
	'eoc-schedules',
	'eoc-team-alerts',
	'eoc-team-alert-detail',
	'eoc-team-alert-issue-syncing',
	'eoc-team-alert-issue-syncing-create',
	'eoc-team-alert-issue-syncing-detail',
	'eoc-team-group-detail',
	'eoc-team-integrations',
	'eoc-team-integration-create',
	'eoc-team-integration-detail',
	'eoc-team-integration-list',
	'eoc-team-integration-log',
	'eoc-team-policies',
	'eoc-who-is-on-call',
	'eoc-global-escalations',
	'communications-status-page',
	'communications-status-page-configuration',
	'communications-status-page-create',
	'communications-status-page-details',
	'communications-status-page-preview',
	'communications-stakeholder-groups',
	'communications-stakeholders',
	'eoc-merge-teams',
	'servicedesk-service-list',
	'eoc-changes',
	'eoc-incidents',
]);

function convert(routeContext: RouterContext<JiraSpaRoute>): SelectedPath {
	const routeName = routeContext.route.name;
	const { query } = routeContext;
	const path: string[] = [MENU_ID_OPERATIONS];
	return convertRouteToPath({ routeName, query, path });
}

function convertNew(args: { routeName: RouteName; query: Query }): SelectedPath {
	const { routeName, query } = args;
	const path: string[] = [MENU_ID_OPERATIONS];
	return convertRouteToPath({ routeName, query, path });
}

function convertRouteToPath(args: {
	routeName: RouteName;
	query: Query;
	path: string[];
}): SelectedPath {
	const { routeName, query, path } = args;
	switch (routeName) {
		case 'eoc-who-is-on-call':
			path.push(MENU_ID_OPS_ON_CALL);
			break;

		case 'eoc-changes':
			path.push(MENU_ID_OPS_CHANGES);
			break;

		case 'eoc-incidents':
			path.push(MENU_ID_OPS_INCIDENTS);
			break;

		case 'eoc-global-alerts':
		case 'eoc-global-alerts-redirect':
		case 'eoc-global-alert-detail': {
			path.push(MENU_ID_OPS_ALERTS);
			const { query: alarmQueue = '', view, filter: savedSearchId } = query;
			view && path.push(ENTITY_ID.ALARM_QUEUE(savedSearchId || alarmQueue));
			break;
		}

		case 'eoc-global-reports':
		case 'eoc-global-report-dashboard':
		case 'eoc-reports-unsubscribe':
			path.push(MENU_ID_OPS_REPORTS);
			break;

		case 'communications-status-page':
		case 'communications-status-page-configuration':
		case 'communications-status-page-create':
		case 'communications-status-page-details':
			path.push(MENU_ID_OPS_STATUS_PAGES);
			break;

		case 'eoc-automations':
		case 'eoc-global-group-detail': // This might be wrong, but I can't see the feature.
		case 'eoc-group-policy':
		case 'eoc-heartbeats':
		case 'eoc-maintenance':
		case 'eoc-permissions':
		case 'eoc-on-call':
		case 'eoc-on-call-redirect':
		case 'eoc-overview':
		case 'eoc-overview-all-teams':
		case 'eoc-reports':
		case 'eoc-reports-team':
		case 'eoc-schedules':
		case 'eoc-team-group-detail':
		case 'eoc-team-alerts':
		case 'eoc-team-alert-detail':
		case 'eoc-team-policies':
		case 'eoc-team-alert-issue-syncing':
		case 'eoc-team-alert-issue-syncing-create':
		case 'eoc-team-alert-issue-syncing-detail':
		case 'eoc-team-integrations':
		case 'eoc-team-integration-create':
		case 'eoc-team-integration-detail':
		case 'eoc-team-integration-list':
		case 'eoc-team-integration-log':
			path.push(MENU_ID_OPS_OVERVIEW);
			break;

		case 'communications-stakeholder-groups':
			path.push(MENU_ID_COMMUNICATIONS_STAKEHOLDERS);
			break;
		case 'communications-stakeholders':
			path.push(MENU_ID_COMMUNICATIONS_STAKEHOLDERS);
			break;

		case 'servicedesk-service-list':
			path.push(MENU_ID_OPS_SERVICES);
			break;

		default: {
			break;
		}
	}

	return path;
}

export const operationsConverter: {
	routes: Set<RouteName>;
	convert: typeof convert;
	convertNew: typeof convertNew;
} = { routes, convert, convertNew };
